import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { openUrl } from "../../../../utils/extension";
import { ExcludeReason, useImportBotMeetingMutation } from "../../../graphql";
import { getErrorContent } from "../importMeetingErrors";
import {
  IntegrationMessage,
  IntegrationMessageProps,
  VideoIntegration,
} from "../IntegrationMessage";
import { LoadingPlaceholder } from "./LoadingPlaceholder";
import { ImportMeetingParams } from "./utils";

type ImportBotMeetingProps = Pick<
  ImportMeetingParams,
  | "meetingUrl"
  | "botMeetingId"
  | "entryPoint"
  | "forceImport"
  | "inZoomApp"
  | "inTeamsApp"
> & {
  videoIntegration: VideoIntegration;
  onError?: (error: string) => void;
};

export const ImportBotMeeting: React.FC<ImportBotMeetingProps> = ({
  meetingUrl,
  botMeetingId,
  entryPoint,
  forceImport,
  inZoomApp,
  inTeamsApp,
  videoIntegration,
  onError,
}) => {
  const navigate = useNavigate();

  const [message, setMessage] = useState<IntegrationMessageProps | null>(null);
  const [importRetryCount, setImportRetryCount] = useState(0);

  const isZoom = videoIntegration === VideoIntegration.ZOOM;
  const shouldOpen =
    !inZoomApp &&
    !inTeamsApp &&
    (importRetryCount === 0 ||
      isZoom ||
      videoIntegration === VideoIntegration.TEAMS);

  const tryOpenMeeting = async (): Promise<void> => {
    if (importRetryCount === 0 && !inZoomApp && !inTeamsApp) {
      try {
        await openUrl(meetingUrl);
      } catch {
        // failed to open the meeting with the extension
      }
    }
  };

  const [importBotMeeting] = useImportBotMeetingMutation({
    variables: { meetingUrl, botMeetingId, entryPoint, forceImport },
    onCompleted: (data) => {
      tryOpenMeeting();

      const botMeeting = data?.importBotMeeting?.botMeeting;
      if (!botMeeting) {
        setMessage(null);
        return;
      }

      const uiMessage: IntegrationMessageProps = {
        primaryMessage: "",
        secondaryMessage: "",
        isError: false,
        videoIntegration,
        videoMeetingUrl: meetingUrl,
        showLaunchButton: shouldOpen,
      };

      if (isZoom && !inZoomApp && botMeeting.zoomAppAutoOpenEnabled) {
        uiMessage.primaryMessage =
          "The Interview Assistant will open automatically in Zoom once you've joined the meeting.";
        uiMessage.secondaryMessage = "Feel free to close this window.";
        uiMessage.isError = true;
        uiMessage.showLaunchButton = true;
        uiMessage.showImportButton = false;
      } else if (botMeeting.exclude) {
        uiMessage.primaryMessage =
          "BrightHire is not scheduled to record the interview.";
        uiMessage.secondaryMessage = `Someone in this meeting ${
          botMeeting.excludeReason === ExcludeReason.GlobalExclude
            ? "is on the recording exclusion list in BrightHire."
            : "opted out of BrightHire."
        }`;
        uiMessage.isError = true;
      } else if (!botMeeting.importMeeting) {
        uiMessage.primaryMessage =
          "BrightHire is not scheduled to record the interview.";
        uiMessage.isError = true;
        uiMessage.showImportButton = true;
      } else if (
        botMeeting.virtualOnsite?.isScheduledInterviewer &&
        !botMeeting.scheduledInterview?.isInterviewer
      ) {
        navigate(`/virtual-onsite/${botMeeting.virtualOnsite.id}`);
        uiMessage.primaryMessage = "Taking you to your meeting...";
      } else if (botMeeting.callId) {
        navigate(`/${botMeeting.callId}`);
        uiMessage.primaryMessage = "Taking you to your meeting...";
      } else {
        uiMessage.primaryMessage = `The BrightHire Notetaker is scheduled to join the interview.`;
        uiMessage.secondaryMessage = shouldOpen
          ? `If your video conference did not open, click below.`
          : `Once BrightHire Notetaker is let in, we'll record the meeting and load your BrightHire experience.`;
        uiMessage.launchButtonMessage = shouldOpen
          ? `Once the meeting opens and the BrightHire notetaker is admitted, we'll record your meeting and load your BrightHire experience.`
          : undefined;

        if (importRetryCount < 2) {
          setImportRetryCount((c) => c + 1);
          importBotMeeting();
        }
      }

      setMessage(uiMessage);
    },
    onError: (error) => {
      tryOpenMeeting();
      onError?.(error.message);

      const errorMessage = getErrorContent(error.message);
      setMessage({
        primaryMessage: errorMessage.primary,
        secondaryMessage: errorMessage.secondary,
        isError: true,
        videoIntegration,
        videoMeetingUrl: meetingUrl,
        showLaunchButton: shouldOpen,
      });
    },
  });

  useEffect(() => {
    importBotMeeting();
  }, []);

  if (
    message?.primaryMessage === "There isn't a meeting we can join." &&
    inTeamsApp
  ) {
    return <Navigate to="/join" />;
  }

  if (message) {
    return (
      <IntegrationMessage
        {...message}
        inZoomApp={inZoomApp}
        inTeamsApp={inTeamsApp}
      />
    );
  }

  return <LoadingPlaceholder />;
};
